import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { BLOG_CATEGORIES, BLOG_AUTHOR } from '../data/blogConfig';
import { fallbackToOriginal } from '../utils/cloudinary';
import '../pages/BlogPost.css';

/**
 * The body of a blog post (2026-10-16). Used by the public page (BlogPost)
 * and by the admin preview (BlogPreview), so a preview looks exactly like the
 * live page. Pack 2's server renderer prints the same structure.
 */

// Sanitize HTML using DOMPurify — battle-tested against all known XSS vectors
export const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'a', 'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup',
      'img', 'figure', 'figcaption', 'video', 'source',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'section', 'article'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style', 'colspan', 'rowspan', 'type', 'controls'
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  });
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'long', day: 'numeric' }) : '');

/** "Updated" only for a real content change at least a day after publishing. */
function updatedDate(post) {
  if (!post.dateModified || !post.publishedAt) return null;
  const gap = new Date(post.dateModified) - new Date(post.publishedAt);
  return gap > 24 * 3600 * 1000 ? post.dateModified : null;
}

const Icon = ({ d }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    {[].concat(d).map((p, i) => <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={p} />)}
  </svg>
);

function BlogArticle({ post, relatedPosts = [], onShare, preview = false }) {
  const author = post.authorProfile || BLOG_AUTHOR;
  const updated = updatedDate(post);
  const faq = (post.faq || []).filter(f => f && f.q && f.a);
  const sources = (post.sources || []).filter(s => s && s.url);
  const aboutPath = (author.url || '').replace(/^https:\/\/www\.utahrealtyphotos\.com/, '') || '/about';

  return (
    <div className="blog-post-page">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <div className="container">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/blog">Blog</Link>
          <span className="separator">/</span>
          <span className="current">{post.title}</span>
        </div>
      </div>

      {/* Featured Image */}
      {post.featuredImage && post.featuredImage.url && (
        <div className="featured-image">
          {/* The API sends the one hero size (c_limit,w_1200,q_auto:eco,f_auto);
              used as-is so each image makes a single derived file. */}
          <img
            src={post.featuredImage.url}
            onError={post.featuredImageOriginal ? fallbackToOriginal(post.featuredImageOriginal.url) : undefined}
            alt={post.featuredImage.alt || post.title}
            fetchpriority="high"
          />
        </div>
      )}

      {/* Post Header */}
      <header className="post-header">
        <div className="container">
          {BLOG_CATEGORIES[post.category] && (
            <div className="post-category">
              <Link to={`/blog?category=${post.category}`}>{BLOG_CATEGORIES[post.category]}</Link>
            </div>
          )}

          <h1>{post.title}</h1>

          <div className="post-meta">
            <div className="meta-item">
              <Icon d={['M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z']} />
              <span className="post-byline">
                By <Link to={aboutPath} rel="author">{author.displayName}</Link>, {author.jobTitle}
              </span>
            </div>

            <div className="meta-item">
              <Icon d={['M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z']} />
              <span>
                <time dateTime={post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined}>{formatDate(post.publishedAt)}</time>
                {updated && <> · Updated <time dateTime={new Date(updated).toISOString()}>{formatDate(updated)}</time></>}
              </span>
            </div>

            <div className="meta-item">
              <Icon d={['M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z']} />
              <span>{post.readTime} min read</span>
            </div>
          </div>
        </div>
      </header>

      {/* Post Content */}
      <article className="post-content">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div
                className="content-body"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.content) }}
              />

              {faq.length > 0 && (
                <section className="post-faq" aria-labelledby="post-faq-heading">
                  <h2 id="post-faq-heading">Frequently asked questions</h2>
                  {faq.map((f, i) => (
                    <div className="post-faq-item" key={i}>
                      <h3>{f.q}</h3>
                      <p>{f.a}</p>
                    </div>
                  ))}
                </section>
              )}

              {sources.length > 0 && (
                <section className="post-sources" aria-labelledby="post-sources-heading">
                  <h2 id="post-sources-heading">Sources</h2>
                  <ul>
                    {sources.map((s, i) => (
                      <li key={i}>
                        <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.url}</a>
                        {s.checkedOn && <span className="post-sources-date"> (checked {formatDate(`${s.checkedOn}T12:00:00Z`)})</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="post-tags">
                  <h4>Tags:</h4>
                  <div className="tags-list">
                    {post.tags.map(tag => (
                      <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`} className="tag">
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Buttons */}
              {!preview && onShare && (
                <div className="post-share">
                  <h4>Share this article:</h4>
                  <div className="share-buttons">
                    <button onClick={() => onShare('facebook')} className="share-btn facebook">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>

                    <button onClick={() => onShare('twitter')} className="share-btn twitter" aria-label="Share on X">
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      X
                    </button>

                    <button onClick={() => onShare('linkedin')} className="share-btn linkedin">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </button>

                    <button onClick={() => onShare('email')} className="share-btn email">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <div className="container">
            <h2>Related Articles</h2>
            <div className="related-posts-grid">
              {relatedPosts.map(related => (
                <article key={related._id || related.slug} className="related-post-card">
                  {related.featuredImage && related.featuredImage.url && (
                    <Link to={`/blog/${related.slug}`} className="related-post-image">
                      <img src={related.featuredImage.url} alt={related.featuredImage.alt || related.title} loading="lazy" width="800" height="450" />
                    </Link>
                  )}
                  <div className="related-post-content">
                    {BLOG_CATEGORIES[related.category] && <div className="related-post-category">{BLOG_CATEGORIES[related.category]}</div>}
                    <h3>
                      <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                    </h3>
                    <p>{related.excerpt}</p>
                    {related.readTime && (
                      <div className="related-post-meta">
                        <span>{related.readTime} min read</span>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="post-cta">
        <div className="container">
          <div className="cta-card">
            <h2>Ready to Showcase Your Property?</h2>
            <p>Get professional photography that makes your listings stand out and sell faster</p>
            <div className="cta-buttons">
              <Link to="/book" className="btn btn-primary btn-lg">
                Book a Session
              </Link>
              <Link to="/pricing" className="btn btn-secondary btn-lg">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogArticle;
